import {isBBoxContained} from '@gaia/hooks/useFitMapToSelectionEffect';
import isPointBehindSidebar from '@gaia/utils/gaia/isPointBehindSidebar';
import type mapboxgl from 'mapbox-gl';
import {useEffect} from 'react';

import useGetPaddingForSelectionEffect, {MapPadding} from './useGetPaddingForSelectionEffect';
import {useLatest} from './useLatest';

interface UseFitMapToFolderEffectOptions {
    interaction: InteractionState;
    folders: Record<string, Folder>;
    activeFolderIds: string[] | undefined;
    map: mapboxgl.Map | undefined;
    controlsExpanded: boolean;
    sidebarWidth: number;
}

export const centerMapOnObject = (
    bounds: [[number, number], [number, number]],
    map: mapboxgl.Map | undefined,
    controlsExpanded: boolean,
    padding: MapPadding
) => {
    if (!bounds || !map) return;
    const mapBounds = map.getBounds();
    const [[minX, minY], [maxX, maxY]] = mapBounds.toArray();

    const [[swLat, swLng], [neLat, neLng]] = bounds;

    const boundsBBox: BBox2d = [swLng, swLat, neLng, neLat];
    if (
        isPointBehindSidebar(map, [swLng, swLat], controlsExpanded) ||
        isPointBehindSidebar(map, [neLng, neLat], controlsExpanded) ||
        !isBBoxContained(boundsBBox, [minX, minY, maxX, maxY])
    ) {
        map.fitBounds(boundsBBox, {
            padding,
            duration: 250,
            maxZoom: 14,
        });
    }
};

export default function useFitMapToFolderEffect({
    interaction,
    folders,
    activeFolderIds,
    map,
    controlsExpanded,
    sidebarWidth,
}: UseFitMapToFolderEffectOptions) {
    const activeFolder = activeFolderIds ? folders[activeFolderIds[0]] : undefined;
    const activeFolderBboxString = activeFolder?.bounds
        ? JSON.stringify(activeFolder.bounds)
        : undefined;
    const padding = useGetPaddingForSelectionEffect(sidebarWidth, controlsExpanded);

    // If a folder is selected and not in the viewport, fit the map to the folder's bounds once it
    // has finished being taken local.
    //
    // Converting bounds to a string prevents triggering the effect when activeFolder changes,
    // since this will create a new bounds array in memory, even though the bounds haven't actually
    // changed.
    //
    const latest = useLatest({controlsExpanded, interaction, map, padding});
    useEffect(() => {
        if (!latest.current.map || latest.current.interaction.type !== 'normal') return;

        if (activeFolderBboxString) {
            // server returns bounds in the order [lat, lng], e.g. [[47.487118, -121.469887], [47.549364, -121.342026]]
            const bounds = JSON.parse(activeFolderBboxString);

            centerMapOnObject(
                bounds,
                latest.current.map,
                latest.current.controlsExpanded,
                latest.current.padding
            );
        }
        // Update only when navigating into or out of a folder, or when a folder's bounds change
    }, [latest, activeFolder?.localId, activeFolderBboxString]);
}
